/**
 * Конструктор по составу пака (WEB-3718; решение владельца 19.10 ~18:40 «не
 * промптом, а кодом»). Состав и смысл групп — `themes/stylePackComposition.ts`.
 *
 * - Новый блок получает настройки пака воронки для своего типа (поведение и
 *   раскладка; цвета, шрифты и размеры даёт форма пака).
 * - Смена пака (решение владельца 23.10 ~04:20). Воронка помнит факт «человек
 *   сам трогал цвета» — признак `colorsTouched` (тема, палитра, фон, цвет
 *   текста). Трогал — смена пака меняет только форму (`stylePackId`), цвета
 *   остаются. Не трогал — меняются форма и цвета: самая весомая тема состава
 *   нового пака. Тексты и экраны смена пака не меняет никогда.
 * - Старая воронка без признака: «не трогал» только если цвета совпадают с
 *   паком — своих палитры, фона и цвета текста нет, а тема не задана или есть
 *   в составе текущего пака; иначе «трогал» (не перекрасить живому клиенту
 *   его цвета).
 * - Кнопка «взять цвета пака» ставит цвета пака и снимает признак.
 * Чат ставит и снимает тот же признак на сервере
 * (`backend/src/ai-funnels/operations/colors-touched.ts`); поля цвета на обоих
 * концах сверяет сторож `colors-touched-ends.web3718.spec.ts`.
 *
 * WEB-3399 — свой стиль организации (`stylePackId: 'custom:<uuid>'`): состав
 * берётся от пака-основы копии — копия из реестра копий по ключу
 * (`compositionPackId`); у `colorsTouched`, где настройки на руках, — правило
 * сервера (`funnelBasePackId`): копия из `settings.customStylePack`, если её
 * `id` равен `stylePackId`, иначе реестр. `withStylePack` снимает копию, когда
 * новый `stylePackId` — не её ключ: так выбор готового стиля или «Без
 * оформления» снимает свой стиль на любом пути (окно карты, окно экрана,
 * старая галерея, `setStylePackId` хранилища).
 */
import { getStylePackComposition } from '@/themes/stylePackComposition';
import type { StylePackBlockSettingValue } from '@/themes/stylePack.types';
import {
  customStyleOf,
  customStylePackById,
  isCustomStylePackRef,
} from '@/themes/customStylePack';

/** Id пака, чей состав действует: у своего стиля — пак-основа копии из реестра. */
function compositionPackId(stylePackId: string | undefined): string | undefined {
  if (!isCustomStylePackRef(stylePackId)) return stylePackId;
  return customStylePackById(stylePackId)?.basePackId;
}

export function packBlockSettings(
  stylePackId: string | undefined,
  blockType: string,
): Record<string, StylePackBlockSettingValue> {
  const packId = compositionPackId(stylePackId);
  if (!packId) return {};
  const own = getStylePackComposition(packId)?.blocks.find(
    (b) => b.type === blockType,
  );
  return own ? { ...own.settings } : {};
}

interface AppearanceSettings {
  stylePackId?: string;
  themeId?: string;
  palette?: unknown;
  background?: unknown;
  textColor?: unknown;
  colorsTouched?: boolean;
  customStylePack?: unknown;
}

/** Поля цвета воронки: их правка человеком — «трогал цвета». */
export const COLOR_SETTING_FIELDS = [
  'themeId',
  'palette',
  'background',
  'textColor',
] as const;

/** Трогал ли человек цвета: признак, а без него — сравнение с паком. */
export function colorsTouched(settings: AppearanceSettings): boolean {
  if (typeof settings.colorsTouched === 'boolean')
    return settings.colorsTouched;
  if (settings.palette || settings.background || settings.textColor)
    return true;
  if (!settings.themeId) return false;
  const packId = isCustomStylePackRef(settings.stylePackId)
    ? 'customStylePack' in settings
      ? customStyleOf(settings as Record<string, unknown>)?.basePackId
      : compositionPackId(settings.stylePackId)
    : settings.stylePackId;
  const pack = packId ? getStylePackComposition(packId) : undefined;
  return !(pack?.palettes.some((p) => p.themeId === settings.themeId) ?? false);
}

/**
 * Самая весомая тема состава пака — её ставит смена пака и «взять цвета». При
 * равных весах — первая по составу (устойчивая сортировка, `[0]`), как на
 * сервере; держат случаи равенства в `style-pack-switch.cases.json` (WEB-3882).
 */
export function packMainThemeId(
  stylePackId: string | undefined,
): string | undefined {
  const packId = compositionPackId(stylePackId);
  const c = packId ? getStylePackComposition(packId) : undefined;
  if (!c || c.palettes.length === 0) return undefined;
  return [...c.palettes].sort((a, b) => b.weight - a.weight)[0].themeId;
}

function withThemeOnly<T extends AppearanceSettings>(
  settings: T,
  themeId: string,
): T {
  const { palette: _p, background: _b, textColor: _t, ...rest } = settings;
  return { ...rest, themeId } as T;
}

/** Настройки после смены пака: форма всегда, цвета — если человек не трогал. */
export function withStylePack<T extends AppearanceSettings>(
  settings: T,
  stylePackId: string | undefined,
): T {
  // Тот же стиль ещё раз (и «Без оформления» у воронки без стиля) не меняет
  // ничего — ни тему, ни цвета; цвета стиля — только кнопкой «Взять цвета
  // стиля» (свежий взгляд волны 2). Те же самые настройки — признак «ничего»
  // для редьюсера и окна стилей.
  if (settings.stylePackId === stylePackId) return settings;
  const { customStylePack: copy, ...rest } = settings;
  const keepCopy =
    copy !== undefined &&
    (copy as { id?: unknown } | null)?.id === stylePackId;
  const next = {
    ...(keepCopy ? settings : rest),
    stylePackId,
  } as T;
  const themeId = packMainThemeId(stylePackId);
  if (themeId === undefined || colorsTouched(settings)) return next;
  return withThemeOnly(next, themeId);
}

/** «Взять цвета пака»: тема пака, свои цвета сняты, признак снят. */
export function withPackColors<T extends AppearanceSettings>(settings: T): T {
  const themeId = packMainThemeId(settings.stylePackId);
  if (themeId === undefined) return settings;
  return { ...withThemeOnly(settings, themeId), colorsTouched: false };
}

/** Человек сам выбрал цвет — признак «трогал». */
export function withColorsTouched<T extends AppearanceSettings>(
  settings: T,
): T {
  return { ...settings, colorsTouched: true };
}

/** Кнопку «взять цвета пака» показывать: у пака есть состав, цвета тронуты. */
export function canTakePackColors(settings: AppearanceSettings): boolean {
  return (
    packMainThemeId(settings.stylePackId) !== undefined &&
    colorsTouched(settings)
  );
}
